import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { getToken, clearToken } from '../lib/auth';
import SecuritySection from '../components/SecuritySection';

interface PhoneStatus {
  phone: string | null;
  phoneVerified: boolean;
}

export default function Settings() {
  const navigate = useNavigate();

  // 密码
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdError, setPwdError] = useState('');

  // 手机号
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<PhoneStatus | null>(null);
  const [sending, setSending] = useState(false);
  const [binding, setBinding] = useState(false);
  const [devCode, setDevCode] = useState('');
  const [phoneMsg, setPhoneMsg] = useState('');
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/login');
      return;
    }
    api<PhoneStatus>('/phone/status').then((res) => {
      if (res.ok && res.data) setStatus(res.data);
    });
  }, [navigate]);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwdError('');
    setPwdMsg('');

    if (newPwd !== confirmPwd) {
      setPwdError('两次输入的新密码不一致');
      return;
    }

    setPwdLoading(true);
    const res = await api('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd }),
    });
    setPwdLoading(false);

    if (res.ok) {
      setPwdMsg('密码已修改，请牢记新密码');
      setOldPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } else {
      setPwdError(errMsg(res, '修改失败'));
    }
  }

  async function sendCode() {
    setPhoneError('');
    setPhoneMsg('');
    setSending(true);

    const res = await api<{ devCode?: string }>('/phone/send', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });

    setSending(false);

    if (res.ok) {
      setPhoneMsg('验证码已发送，5 分钟内有效');
      if (res.data?.devCode) setDevCode(res.data.devCode);
    } else {
      setPhoneError(errMsg(res, '发送失败'));
    }
  }

  async function bindPhone(e: React.FormEvent) {
    e.preventDefault();
    setPhoneError('');
    setPhoneMsg('');
    setBinding(true);

    const res = await api('/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ phone, code }),
    });

    setBinding(false);

    if (res.ok) {
      setPhoneMsg('手机号绑定成功');
      setStatus({ phone, phoneVerified: true });
      setCode('');
      setDevCode('');
    } else {
      setPhoneError(errMsg(res, '验证失败'));
    }
  }

  async function unbind() {
    if (!confirm('确定解绑手机号吗？')) return;
    await api('/phone/unbind', { method: 'POST', body: '{}' });
    setStatus({ phone: null, phoneVerified: false });
    setPhoneMsg('已解绑');
  }

  function logout() {
    clearToken();
    navigate('/');
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-ink-100 to-ink-50 px-6 py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="font-serifcn text-2xl text-ink-900">账号设置</h1>
          <Link
            to="/memorials"
            className="rounded-full border border-ink-300 px-4 py-1.5 text-xs text-ink-700"
          >
            我的纪念馆
          </Link>
        </div>

        {/* 手机号 */}
        <section className="mb-6 rounded-2xl border border-ink-200 bg-white/80 p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-medium text-ink-900">手机号</h2>
            {status?.phone ? (
              <span
                className={`rounded-full px-2.5 py-0.5 text-[10px] ${
                  status.phoneVerified
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-amber-50 text-amber-700'
                }`}
              >
                {status.phoneVerified ? '已验证' : '未验证'}
              </span>
            ) : (
              <span className="rounded-full bg-ink-100 px-2.5 py-0.5 text-[10px] text-ink-500">
                未绑定
              </span>
            )}
          </div>

          {status?.phone && (
            <p className="mb-4 text-sm text-ink-700">
              当前号码：{status.phone.replace(/(\d{3})\d{4}(\d+)/, '$1****$2')}
            </p>
          )}

          <div className="mb-3">
            <label className="mb-1.5 block text-xs text-ink-600">
              {status?.phone ? '更换手机号' : '绑定手机号'}
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+14155552671（含国家码）"
              className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
            />
            <p className="mt-1 text-[11px] text-ink-400">
              需包含国家码，如美国 +1、香港 +852、新加坡 +65
            </p>
          </div>

          <button
            type="button"
            onClick={sendCode}
            disabled={sending || !phone}
            className="mb-3 h-10 rounded-xl border border-ink-300 px-5 text-xs text-ink-700 transition hover:bg-ink-100 disabled:opacity-40"
          >
            {sending ? '发送中…' : '发送验证码'}
          </button>

          {devCode && (
            <p className="mb-3 rounded-lg bg-candle-300/20 px-3 py-2 text-xs text-ink-700">
              开发环境验证码：<span className="font-mono">{devCode}</span>
            </p>
          )}

          <form onSubmit={bindPhone}>
            <div className="mb-3">
              <label className="mb-1.5 block text-xs text-ink-600">验证码</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6 位数字"
                maxLength={6}
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm tracking-widest outline-none focus:border-ink-500 focus:bg-white"
              />
            </div>

            {phoneError && (
              <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                {phoneError}
              </p>
            )}
            {phoneMsg && (
              <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {phoneMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={binding || !code || !phone}
              className="h-11 rounded-xl bg-ink-900 px-6 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-40"
            >
              {binding ? '验证中…' : '确认绑定'}
            </button>

            {status?.phone && (
              <button
                type="button"
                onClick={unbind}
                className="ml-3 h-11 text-xs text-red-600 underline"
              >
                解绑
              </button>
            )}
          </form>
        </section>

        {/* 密保问题 */}
        <SecuritySection />

        {/* 修改密码 */}
        <section className="mb-6 rounded-2xl border border-ink-200 bg-white/80 p-6">
          <h2 className="mb-4 text-sm font-medium text-ink-900">修改密码</h2>
          <form onSubmit={changePassword}>
            <div className="mb-3">
              <label className="mb-1.5 block text-xs text-ink-600">原密码</label>
              <input
                type="password"
                value={oldPwd}
                onChange={(e) => setOldPwd(e.target.value)}
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              />
            </div>
            <div className="mb-3">
              <label className="mb-1.5 block text-xs text-ink-600">新密码</label>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="至少 8 位"
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              />
            </div>
            <div className="mb-4">
              <label className="mb-1.5 block text-xs text-ink-600">确认新密码</label>
              <input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
              />
            </div>

            {pwdError && (
              <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                {pwdError}
              </p>
            )}
            {pwdMsg && (
              <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                {pwdMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={pwdLoading || !oldPwd || !newPwd}
              className="h-11 rounded-xl bg-ink-900 px-6 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-40"
            >
              {pwdLoading ? '提交中…' : '修改密码'}
            </button>
          </form>
        </section>

        <div className="text-center">
          <button onClick={logout} className="text-xs text-ink-500 underline">
            退出登录
          </button>
        </div>
      </div>
    </div>
  );
}
